<template>
    <RedesignBaseCounterCard
        :widget="data"
        :show-text="showText"
        :display-border="displayBorder"
        :card-text="cardText"
        :card-value="cardValue"
        :icon="widgetIcon"
        :icon-color="iconColor"
        :loading="isLoading"
        class="average-calls-duration-card"
    />
</template>

<script setup lang="ts">
/* Average Call Duration Card Widget */
import RedesignBaseCounterCard from '@/components/RedesignComponents/Cards/RedesignBaseCounterCard.vue'
import { WidgetExtended } from '@/types/dashboard'
import { computed } from 'vue'
import useWidgetDataWithRefreshInterval from '@/composables/useWidgetDataWithRefreshInterval'
import { AVERAGE_CALLS_DURATION_ICONS, AVERAGE_DURATION_ALL_CALLS, availableCounters } from '@/enum/queueCounters'
import get from 'lodash/get'
import { activeLayoutColors } from '@/composables/globalData/useLayouts'
import { isObject } from '@/util/objectUtil'
import i18n from '@/i18n'

const props = withDefaults(
    defineProps<{
        data: WidgetExtended
        editable?: boolean
        onEditMode?: boolean
        showText?: boolean
        displayBorder?: boolean
        queue?: boolean
    }>(),
    {
        showText: true,
        displayBorder: true,
    })

const {
    data: widgetData,
    isLoading
} = useWidgetDataWithRefreshInterval(props, 'data')

/* Computed */
const showCounterKey = computed(() => {
    return props.data.WidgetLayout?.showCounter || AVERAGE_DURATION_ALL_CALLS
})

const defaultCardText = computed(() => {
    const entry = availableCounters.find((counter) => counter.value === showCounterKey.value)
    return entry ? i18n.t(entry.label).toString() : showCounterKey.value
})

const cardText = computed(() => {
    const raw = props.data.WidgetLayout?.ContentTitle
    const key = showCounterKey.value
    if (!isObject(raw)) {
        return defaultCardText.value
    }

    const contentText = raw as Record<string, string>
    if (contentText[key] || contentText[key] === '') {
        return contentText[key]
    }
    
    return defaultCardText.value
})

const cardValue = computed(() => {
    const key = showCounterKey.value
    return get(widgetData.value, `[0][${key}]`, '0:00:00')
})
const widgetIcon = computed(() => {
    const type = showCounterKey.value
    const icons = AVERAGE_CALLS_DURATION_ICONS as Record<string, string>
    return icons[type] || 'PhoneCall'
})
const iconColor = computed(() => {
    return props.data.WidgetLayout?.colors?.fonts || activeLayoutColors.value.mainTextColor
})
</script>
